// Route table for the router-driven app shell.
//
// All settings components are verified at full feature parity with dev
// (confirmed by Phase 2b agent audit — 2026-06-07).
// SettingsLayout: NavLink nav shell with admin gate (replaced stub in Phase 2a-1).
// AgentEditorWidget: 5-tab URL-driven Outlet container (replaced stub in Phase 2a-2).

import type { RouteObject } from 'react-router-dom';
import { AppShell, SettingsIndexRedirect, AgentTabsIndexRedirect } from '../AppShell';
import { RouteErrorFallback } from './RouteErrorFallback';
import { LoggedOutPage, LoginPage, LogoutPage, SessionExpiredPage, SkillRegistryPage } from '@/pages';
import ErrorBoundary from '../../components/ErrorBoundary';
import SettingsLayout from '../../components/settings/SettingsLayout';
import { AgentEditorWidget } from '../../widgets/settings/AgentEditorWidget';
import AdminTab from '../../components/settings/admin/AdminTab';
import AgentConfigEditor from '../../components/settings/agent/AgentConfigEditor';
import AgentPromptEditor from '../../components/settings/agent/AgentPromptEditor';
import OwnersTab from '../../components/settings/agent/OwnersTab';
import ProvenanceTab from '../../components/settings/agent/ProvenanceTab';
import AgentsEmptyState from '../../components/settings/AgentsEmptyState';
import ExperimentTabWidget from '../../widgets/settings/agent/ExperimentTabWidget';
import ToolsTabWidget from '../../widgets/settings/agent/ToolsTabWidget';
import AgentSkillsTabWidget from '../../widgets/settings/agent/AgentSkillsTabWidget';
import { SchedulesPage } from '../../pages/schedules';
import { PluginsPage } from '../../pages/plugins';

export const routes: RouteObject[] = [
  {
    path: '/logged-out',
    element: <LoggedOutPage />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/session-expired',
    element: <SessionExpiredPage />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/logout',
    element: <LogoutPage />,
    errorElement: <RouteErrorFallback />,
  },
  {
    element: <AppShell />,
    errorElement: <RouteErrorFallback />,
    children: [
      // Chat routes render nothing — MainView is always-mounted above Outlet
      // and reflects agent/session selection via the URL↔store sync in AppShell.
      { path: '/', element: null },
      { path: '/agents/:agentId', element: null },
      { path: '/agents/:agentId/sessions/:sessionId', element: null },
      {
        path: '/settings',
        element: <ErrorBoundary><SettingsLayout /></ErrorBoundary>,
        children: [
          { index: true, element: <SettingsIndexRedirect /> },
          { path: 'admin', element: <AdminTab /> },
          {
            path: 'agents',
            element: <AgentEditorWidget />,
            children: [
              { index: true, element: <AgentsEmptyState /> },
              { path: ':agentId', element: <AgentTabsIndexRedirect /> },
              { path: ':agentId/config', element: <AgentConfigEditor /> },
              { path: ':agentId/prompt', element: <AgentPromptEditor /> },
              { path: ':agentId/experiment', element: <ExperimentTabWidget /> },
              { path: ':agentId/tools', element: <ToolsTabWidget /> },
              { path: ':agentId/owners', element: <OwnersTab /> },
              { path: ':agentId/skills', element: <AgentSkillsTabWidget /> },
              { path: ':agentId/provenance', element: <ProvenanceTab /> },
            ],
          },
        ],
      },
      { path: '/schedules', element: <SchedulesPage /> },
      { path: '/skills', element: <SkillRegistryPage /> },
      { path: '/plugins', element: <PluginsPage /> },
    ],
  },
];
